const CHANNEL_NAME = 'verdekai-window-session';
const HEARTBEAT_INTERVAL_MS = 5000;
const WINDOW_TTL_MS = 15000;
const ACTIVITY_BROADCAST_THROTTLE_MS = 1000;

export const WINDOW_SESSION_MESSAGE_TYPES = {
  WINDOW_HELLO: 'WINDOW_HELLO',
  WINDOW_HEARTBEAT: 'WINDOW_HEARTBEAT',
  WINDOW_GOODBYE: 'WINDOW_GOODBYE',
  ACTIVITY_PING: 'ACTIVITY_PING',
  WINDOW_COUNT_UPDATE: 'WINDOW_COUNT_UPDATE'
};

class WindowSessionCoordinator {
  constructor() {
    this.channel = null;
    this.initialized = false;
    this.windowId = `window-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    this.windowState = new Map();
    this.windowCountListeners = new Set();
    this.activityListeners = new Set();
    this.currentWindowCount = 1;
    this.heartbeatTimer = null;
    this.lastActivityBroadcastAt = 0;
    this.beforeUnloadHandler = null;
  }

  initialize() {
    if (this.initialized) return;
    this.initialized = true;

    if (typeof BroadcastChannel === 'undefined') {
      this.emitWindowCount(1);
      return;
    }

    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = (event) => this.handleMessage(event);
      this.channel.onmessageerror = (error) => {
        console.error('Window session channel error:', error);
      };

      this.registerSelf();
      this.broadcast(WINDOW_SESSION_MESSAGE_TYPES.WINDOW_HELLO);

      this.heartbeatTimer = window.setInterval(() => {
        this.registerSelf();
        this.pruneStaleWindows();
        this.broadcast(WINDOW_SESSION_MESSAGE_TYPES.WINDOW_HEARTBEAT);
      }, HEARTBEAT_INTERVAL_MS);

      this.beforeUnloadHandler = () => {
        this.broadcast(WINDOW_SESSION_MESSAGE_TYPES.WINDOW_GOODBYE);
      };
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    } catch (error) {
      console.error('Failed to initialize window session coordinator:', error);
      this.channel = null;
      this.emitWindowCount(1);
    }
  }

  destroy() {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }

    if (this.heartbeatTimer) {
      window.clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }

    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    this.windowState.clear();
    this.initialized = false;
    this.currentWindowCount = 1;
    this.emitWindowCount(1);
  }

  subscribeWindowCount(callback) {
    this.windowCountListeners.add(callback);
    callback(this.currentWindowCount);
    return () => this.windowCountListeners.delete(callback);
  }

  subscribeActivity(callback) {
    this.activityListeners.add(callback);
    return () => this.activityListeners.delete(callback);
  }

  publishActivity() {
    if (!this.initialized) return;

    const now = Date.now();
    if (now - this.lastActivityBroadcastAt < ACTIVITY_BROADCAST_THROTTLE_MS) {
      return;
    }

    this.lastActivityBroadcastAt = now;
    this.emitActivity();
    this.broadcast(WINDOW_SESSION_MESSAGE_TYPES.ACTIVITY_PING);
  }

  getWindowCount() {
    return this.currentWindowCount;
  }

  isReady() {
    return this.initialized;
  }

  registerSelf() {
    this.windowState.set(this.windowId, Date.now());
    this.emitWindowCount(this.getActiveWindowCount());
    this.broadcast(WINDOW_SESSION_MESSAGE_TYPES.WINDOW_COUNT_UPDATE, {
      windowCount: this.getActiveWindowCount()
    });
  }

  pruneStaleWindows() {
    const now = Date.now();
    let changed = false;

    this.windowState.forEach((lastSeenAt, id) => {
      if (now - lastSeenAt > WINDOW_TTL_MS) {
        this.windowState.delete(id);
        changed = true;
      }
    });

    if (changed) {
      this.emitWindowCount(this.getActiveWindowCount());
      this.broadcast(WINDOW_SESSION_MESSAGE_TYPES.WINDOW_COUNT_UPDATE, {
        windowCount: this.getActiveWindowCount()
      });
    }
  }

  getActiveWindowCount() {
    return Math.max(1, this.windowState.size || 1);
  }

  emitWindowCount(count) {
    this.currentWindowCount = count;
    this.windowCountListeners.forEach((listener) => {
      try {
        listener(count);
      } catch (error) {
        console.error('Error in window count listener:', error);
      }
    });
  }

  emitActivity() {
    this.activityListeners.forEach((listener) => {
      try {
        listener();
      } catch (error) {
        console.error('Error in activity listener:', error);
      }
    });
  }

  handleMessage(event) {
    const { type, sourceWindowId, timestamp, payload } = event.data || {};
    if (!type || sourceWindowId === this.windowId) {
      return;
    }

    if (type === WINDOW_SESSION_MESSAGE_TYPES.WINDOW_HELLO || type === WINDOW_SESSION_MESSAGE_TYPES.WINDOW_HEARTBEAT) {
      this.windowState.set(sourceWindowId, timestamp || Date.now());
      this.pruneStaleWindows();
      this.emitWindowCount(this.getActiveWindowCount());
      return;
    }

    if (type === WINDOW_SESSION_MESSAGE_TYPES.WINDOW_GOODBYE) {
      this.windowState.delete(sourceWindowId);
      this.pruneStaleWindows();
      this.emitWindowCount(this.getActiveWindowCount());
      return;
    }

    if (type === WINDOW_SESSION_MESSAGE_TYPES.ACTIVITY_PING) {
      this.windowState.set(sourceWindowId, timestamp || Date.now());
      this.emitActivity();
      return;
    }

    if (type === WINDOW_SESSION_MESSAGE_TYPES.WINDOW_COUNT_UPDATE) {
      const localCount = this.getActiveWindowCount();
      const reportedCount = Number(payload?.windowCount) || localCount;
      this.emitWindowCount(Math.max(localCount, reportedCount));
    }
  }

  broadcast(type, payload = {}) {
    if (!this.channel) return;

    try {
      this.channel.postMessage({
        type,
        payload,
        sourceWindowId: this.windowId,
        timestamp: Date.now()
      });
    } catch (error) {
      console.error('Failed to broadcast window session message:', error);
    }
  }
}

export const windowSessionCoordinator = new WindowSessionCoordinator();
export default windowSessionCoordinator;
